// src/cockpit4/components/KnowledgeGraphPanel.tsx
// ============================================
// P1+ 知识图谱可视化（实体→关系→可被 AI 引用的结构化知识）
//   数据源：RuntimeManager.query(KnowledgeGraph, {type:'get_graph'})
//   渲染：统计 + SVG 图（按节点类型分列，边连线）+ 节点列表
// ============================================

import { useEffect, useState, useMemo } from "react";
import { Share2, Loader2, Network } from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

interface GraphNode { id: string; type: string; label?: string; [k: string]: any; }
interface GraphEdge { source: string; target: string; type?: string; [k: string]: any; }
interface GraphData { nodes: GraphNode[]; edges: GraphEdge[]; metadata?: { totalNodes?: number; totalEdges?: number; byType?: Record<string, number> }; }

const TYPE_COLORS: Record<string, string> = {
  customer: "#3B82F6", sku: "#10B981", sop: "#F59E0B", workflow: "#A855F7",
  memory: "#06B6D4", content: "#EC4899", project: "#8B5CF6", meeting: "#EF4444",
  tag: "#6B7280", person: "#22D3EE", brand: "#F97316",
};
const TYPE_LABEL: Record<string, string> = {
  customer: "客户", sku: "产品", sop: "SOP", workflow: "工作流", memory: "记忆",
  content: "内容", project: "项目", meeting: "会议", tag: "标签", person: "联系人", brand: "品牌",
};

export default function KnowledgeGraphPanel() {
  const [graph, setGraph] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selNode, setSelNode] = useState<string>("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const resp = await RuntimeManager.query(RuntimeName.KnowledgeGraph as any, { type: "get_graph" } as any);
        setGraph((resp?.data as GraphData | null) || null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // 按类型分列布局（每列一类型，节点纵向排列）
  const layout = useMemo(() => {
    if (!graph?.nodes) return { positions: new Map<string, { x: number; y: number }>(), types: [] as string[] };
    const types = Array.from(new Set(graph.nodes.map((n) => n.type)));
    const colW = 130;
    const rowH = 38;
    const maxPerCol = 14;
    const positions = new Map<string, { x: number; y: number }>();
    types.forEach((t, ci) => {
      const ns = graph.nodes.filter((n) => n.type === t).slice(0, maxPerCol);
      ns.forEach((n, ri) => positions.set(n.id, { x: 40 + ci * colW, y: 30 + ri * rowH }));
    });
    return { positions, types };
  }, [graph]);

  const neighbors = useMemo(() => {
    if (!graph || !selNode) return [];
    return graph.edges
      .filter((e) => e.source === selNode || e.target === selNode)
      .map((e) => (e.source === selNode ? e.target : e.source))
      .slice(0, 20);
  }, [graph, selNode]);

  if (loading) {
    return <div className="flex items-center gap-2 text-[11px] text-gray-500 py-6 justify-center"><Loader2 size={12} className="animate-spin" /> 正在构建知识图谱…</div>;
  }
  if (error) {
    return <div className="text-[11px] text-red-400 py-4 text-center">知识图谱加载失败：{error}</div>;
  }
  if (!graph || !graph.nodes || graph.nodes.length === 0) {
    return <div className="text-[11px] text-gray-600 py-6 text-center">知识图谱暂无节点。在知识中心录入客户/产品/SOP/内容后，图谱会自动构建。</div>;
  }

  const meta = graph.metadata || {};
  const totalN = meta.totalNodes ?? graph.nodes.length;
  const totalE = meta.totalEdges ?? graph.edges.length;
  const byType = meta.byType || {};

  return (
    <div className="space-y-2">
      <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
        <Network size={11} className="text-cyan-400" />
        知识图谱：<span className="text-white">{totalN}</span> 节点 / <span className="text-white">{totalE}</span> 关系 — 实体→关系→可被 AI 引用的结构化知识
      </div>

      {/* 统计卡 */}
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(byType).map(([t, c]) => (
          <span key={t} className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: `${TYPE_COLORS[t] || "#6B7280"}15`, color: TYPE_COLORS[t] || "#6B7280" }}>
            {TYPE_LABEL[t] || t} {c}
          </span>
        ))}
      </div>

      {/* SVG 图 */}
      <div className="rounded border border-white/[0.06] bg-white/[0.02] overflow-x-auto">
        <svg width={Math.max(400, layout.types.length * 130)} height={420} style={{ display: "block" }}>
          {/* 边 */}
          {graph.edges.slice(0, 200).map((e, i) => {
            const s = layout.positions.get(e.source);
            const t = layout.positions.get(e.target);
            if (!s || !t) return null;
            return <line key={i} x1={s.x} y1={s.y} x2={t.x} y2={t.y} stroke="rgba(120,180,255,0.15)" strokeWidth={0.5} />;
          })}
          {/* 节点 */}
          {graph.nodes.slice(0, 100).map((n) => {
            const p = layout.positions.get(n.id);
            if (!p) return null;
            const color = TYPE_COLORS[n.type] || "#6B7280";
            const sel = selNode === n.id;
            return (
              <g key={n.id} onClick={() => setSelNode(sel ? "" : n.id)} style={{ cursor: "pointer" }}>
                <circle cx={p.x} cy={p.y} r={sel ? 6 : 4} fill={color} stroke={sel ? "#fff" : "none"} strokeWidth={1} />
                <title>{n.label || n.id}</title>
                <text x={p.x + 7} y={p.y + 3} fontSize={8} fill="#9aa7b5" fontFamily="monospace">
                  {(n.label || n.id).slice(0, 8)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* 选中节点的邻居 */}
      {selNode && (
        <div className="rounded border border-cyan-500/20 bg-cyan-500/[0.03] p-2">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-mono text-cyan-300">选中：{graph.nodes.find((n) => n.id === selNode)?.label || selNode}</span>
            <button onClick={() => setSelNode("")} className="ml-auto text-[9px] text-gray-500">清除</button>
          </div>
          {neighbors.length === 0 ? (
            <p className="text-[9px] text-gray-600">无关联节点</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {neighbors.map((id) => {
                const n = graph.nodes.find((x) => x.id === id);
                return (
                  <span key={id} className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: `${TYPE_COLORS[n?.type || "tag"] || "#6B7280"}15`, color: TYPE_COLORS[n?.type || "tag"] || "#6B7280" }}>
                    {n?.label || id}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
      <p className="text-[8px] font-mono text-gray-600">点节点查看关联实体；图为知识中心真实数据自动构建（客户→购买→产品→SOP→内容→记忆）。</p>
    </div>
  );
}
